/**
 * @fileoverview "+ Add viewer" — give someone read-only sight of a program.
 *
 * A viewer is a sponsor, a school coordinator, a department head: someone who
 * needs to see how a program is going without being able to change anything.
 * They sign in at the org portal like an administrator does, but every
 * administrator route refuses them, and they see only the programs named here.
 *
 * Every viewer sees what a program is and when it meets: its classes and its
 * calendar (Oct 2026). Everything else is granted one at a time and starts
 * empty, so adding a viewer and forgetting to tick anything reveals the
 * timetable rather than everything; the dialog says so above the ticks.
 *
 * Adding takes two pages. The first is who they are and what they may see;
 * Continue saves nothing. The second asks how their invitation reaches them:
 * a link the administrator shares themselves (Hans sends nothing), or an
 * email from Hans. Only that choice adds the viewer, so closing the dialog or
 * going Back before it shares nothing and adds nobody.
 *
 * No password is set here: the viewer's invitation link (good for 30 days)
 * lets them choose their own, and the account cannot be signed into until
 * they do. The list behind refreshes when the dialog closes.
 *
 * Passed a `viewer`, the same dialog edits one: their programs and their
 * permissions. The email and name are who they are and are not edited here.
 * A viewer who has not used their link yet can be sent a fresh one, by Hans
 * or by the administrator; either way the old link stops working.
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Button, ButtonLink, Icon } from "@/components/studio/primitives";

/** Mirrors VIEWER_PERMISSIONS on the server — an option the model would
    reject is a dead checkbox, so the two lists must stay in step. */
export const VIEWER_PERMISSION_OPTIONS = [
  { id: "weekly_recap", label: "Weekly recap email", desc: "What was taught last week." },
  { id: "week_ahead", label: "Week ahead email", desc: "What is scheduled next week." },
  { id: "performance", label: "Program performance", desc: "Totals and per-instructor teaching." },
  { id: "attendance", label: "Attendance", desc: "Who attended each session." },
  { id: "roster", label: "Roster", desc: "The students enrolled in the program." },
  {
    id: "instructor_contacts",
    label: "Instructor contact information",
    desc: "The email and phone number of each instructor teaching in the program.",
  },
  {
    id: "roster_upload",
    label: "Upload student rosters",
    desc: "Import a class roster. Students arrive with no recording consent, exactly as when you import one.",
  },
  {
    id: "lesson_notes",
    label: "Lesson notes",
    desc: "The notes instructors send families after each session. Never for a student under 13: those go to parents only.",
  },
];

/** "Roster, Instructor contact information" — what a viewer holds, in words. */
export function permissionLabels(ids = []) {
  return VIEWER_PERMISSION_OPTIONS.filter((o) => ids.includes(o.id)).map((o) => o.label);
}

const inputClass =
  "w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-[10px] font-sans text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent-yellow)]";

/** "A", "A and B", "A, B and C". */
function listed(titles) {
  if (titles.length < 2) return titles[0] || "";
  return `${titles.slice(0, -1).join(", ")} and ${titles[titles.length - 1]}`;
}

/**
 * The invitation as the administrator might send it from their own email:
 * the link, who it is for and what to do with it. Their mail app opens with
 * it ready to edit; nothing is sent from here.
 */
export function inviteMailto({ email, firstName, programTitles = [], url }) {
  const what = listed(programTitles) || "our program";
  const subject = `Your access to ${what} on Hans`;
  const body = [
    firstName ? `Hi ${firstName},` : "Hi,",
    `You can now follow ${what} on Hans. Choose your password with this link, then sign in with this email address:`,
    url,
    "The link works once and expires in 30 days.",
  ].join("\n\n");
  return `mailto:${email || ""}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/** An invitation link to copy, or to open in the administrator's mail app. */
function InviteLink({ url, email, firstName, programTitles, note }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy the link. Select it and copy it yourself.");
    }
  };

  return (
    <div className="mt-3 rounded-[14px] border border-[var(--studio-divider)] bg-[var(--studio-base)] p-4" data-testid="viewer-invite-link">
      <input
        readOnly
        value={url}
        onFocus={(e) => e.target.select()}
        // Inside the edit form, Enter here would save the viewer.
        onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
        aria-label="Invitation link"
        data-testid="viewer-invite-url"
        className="w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-3 py-2 font-mono text-[13px] text-[var(--text-primary)] outline-none"
      />
      <div className="mt-3 flex flex-wrap gap-2">
        <Button type="button" icon={copied ? "check" : "copy"} onPress={copy} data-testid="viewer-invite-copy">
          {copied ? "Copied" : "Copy link"}
        </Button>
        <ButtonLink href={inviteMailto({ email, firstName, programTitles, url })} icon="mail" data-testid="viewer-invite-email">
          Email it
        </ButtonLink>
      </div>
      <p className="mt-3 font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="viewer-invite-note">
        {note}
      </p>
    </div>
  );
}

/** The two ways an invitation can reach a new viewer, in the order offered. */
const DELIVERY = [
  {
    id: "link",
    icon: "link",
    title: "Share a link",
    busy: "Making the link…",
    desc: () => "Copy a link to send from your own email or messages. Hans sends nothing.",
  },
  {
    id: "email",
    icon: "mail",
    title: "Send from Hans",
    busy: "Sending…",
    desc: (email) => `Hans emails ${email} a link to choose a password.`,
  },
];

/**
 * The second page of adding a viewer: who and what, read back, and the two
 * ways to invite them. Nothing has been saved yet, and the page says so.
 */
function ChooseDelivery({ name, email, programTitles, grants, choosing, onChoose, onBack }) {
  return (
    <div data-testid="viewer-send-choice">
      <p className="font-sans text-[15px] text-[var(--text-primary)]" data-testid="viewer-send-who">
        {name || email}
        {name ? <span className="text-[var(--text-tertiary)]"> · {email}</span> : null}
      </p>
      <p className="mt-1 font-sans text-[14px] text-[var(--text-secondary)]" data-testid="viewer-send-what">
        {grants.length
          ? `Sees ${listed(programTitles)}. Also allowed: ${grants.join(", ")}.`
          : `Sees ${listed(programTitles)}: its classes and calendar only.`}
      </p>

      <div className="mt-5 flex flex-col gap-3">
        {DELIVERY.map((d) => (
          <button
            key={d.id}
            type="button"
            onClick={() => onChoose(d.id)}
            disabled={Boolean(choosing)}
            data-testid={`viewer-send-${d.id}`}
            className="flex w-full items-start gap-4 rounded-[14px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] p-4 text-left transition-colors hover:bg-[var(--studio-overlay)] focus-visible:border-[var(--accent-yellow)] focus-visible:outline-none disabled:cursor-default disabled:opacity-60"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--studio-interactive)] text-[var(--text-secondary)]">
              <Icon name={d.icon} size={18} />
            </span>
            <span>
              <span className="block font-sans text-[15px] font-medium text-[var(--text-primary)]">
                {choosing === d.id ? d.busy : d.title}
              </span>
              <span className="mt-0.5 block font-sans text-[13px] text-[var(--text-tertiary)]">{d.desc(email)}</span>
            </span>
          </button>
        ))}
      </div>

      <p className="mt-4 font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="viewer-send-nothing-yet">
        Nothing is sent, and they aren&rsquo;t added, until you choose.
      </p>
      <div className="mt-4">
        <Button type="button" variant="ghost" icon="chevronLeft" onPress={onBack} disabled={Boolean(choosing)} data-testid="viewer-send-back">
          Back
        </Button>
      </div>
    </div>
  );
}

/** Shown once a viewer is added: how their invitation went, and the link when it is theirs to send. */
function Invited({ name, email, url, via, emailSent, programTitles, firstName, onDone }) {
  const who = name ? `${name} (${email})` : email;
  const emailed = via === "email" && emailSent;
  return (
    <div data-testid="viewer-invited">
      <p className="font-sans text-[15px] text-[var(--text-primary)]" data-testid="viewer-invited-email">
        {emailed
          ? `We've emailed ${who} a link to choose a password.`
          : via === "email"
            ? `We couldn't email ${email}. Send them this link from your own email.`
            : `${name || email} is added. Hans hasn't emailed them: send them this link.`}
      </p>
      {emailed ? (
        <p className="mt-1 font-sans text-[14px] text-[var(--text-secondary)]">
          It works once, within 30 days. If it doesn&rsquo;t arrive, open their row to send it again or share a link.
        </p>
      ) : (
        <InviteLink url={url} email={email} firstName={firstName} programTitles={programTitles} note="It works once, within 30 days." />
      )}
      <div className="mt-5">
        <Button type="button" variant="ghost" onPress={onDone} data-testid="viewer-invited-done">
          Done
        </Button>
      </div>
    </div>
  );
}

export default function AddViewerDialog({ open, onOpenChange, programs = [], defaultProgramId, onAdded, viewer = null }) {
  const editing = Boolean(viewer);
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "" });
  const [permissions, setPermissions] = useState(() => new Set());
  const [scope, setScope] = useState(() => new Set(defaultProgramId ? [defaultProgramId] : []));
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);
  const [linking, setLinking] = useState(false);
  // Adding: "details" (who and what), then "send" (how they are invited).
  const [step, setStep] = useState("details");
  // Which way of inviting them is being carried out, while it is.
  const [choosing, setChoosing] = useState(null);
  // The viewer just added and how they were invited, shown in place of the form.
  const [added, setAdded] = useState(null);
  // A pending viewer's newest link, once one is made here.
  const [link, setLink] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep("details");
    setChoosing(null);
    setAdded(null);
    setLink(null);
    if (viewer) {
      setForm({ email: viewer.email || "", first_name: viewer.first_name || "", last_name: viewer.last_name || "" });
      setPermissions(new Set(viewer.permissions || []));
      setScope(new Set(viewer.program_ids || []));
      return;
    }
    setForm({ email: "", first_name: "", last_name: "" });
    setPermissions(new Set());
    setScope(new Set(defaultProgramId ? [defaultProgramId] : []));
  }, [open, defaultProgramId, viewer]);

  // The list behind is refreshed on the way out, not while the link is up:
  // reloading the page would take the dialog down with it.
  const close = () => {
    onOpenChange(false);
    if (added) onAdded?.(added.viewer);
  };
  const programTitles = programs.filter((p) => scope.has(p.program_id)).map((p) => p.title);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const flip = (setter) => (id) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const togglePermission = flip(setPermissions);
  const toggleProgram = flip(setScope);

  const resendInvite = async () => {
    setResending(true);
    try {
      const res = await orgApi.post(`/org/viewers/${viewer.admin_id}/resend-invite`);
      const sent = res.data?.email_sent !== false;
      if (sent) toast.success(`New invitation sent to ${viewer.email}`);
      else toast.error(`Couldn't email ${viewer.email}. Send them the link yourself, or try again in a minute.`);
      // Any link shown before has just stopped working: show the new one.
      if (res.data?.invite_url) {
        setLink({
          url: res.data.invite_url,
          note: sent
            ? "The link we just emailed. It works once, within 30 days."
            : "It works once, within 30 days. Any link sent before no longer works.",
        });
      }
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't resend the invitation"));
    } finally {
      setResending(false);
    }
  };

  const newLink = async () => {
    setLinking(true);
    try {
      const res = await orgApi.post(`/org/viewers/${viewer.admin_id}/invite-link`);
      setLink({
        url: res.data.invite_url,
        note: "A new link: the one sent before no longer works. It works once, within 30 days.",
      });
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't make a new link"));
    } finally {
      setLinking(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) {
      toast.error("Enter an email address for the viewer");
      return;
    }
    if (scope.size === 0) {
      toast.error("Choose at least one program for them to see");
      return;
    }
    if (!editing) {
      // Nothing is saved yet: the viewer is added by the choice on the next page.
      setStep("send");
      return;
    }
    setSaving(true);
    try {
      // Programs outside this dialog's list are kept: an edit from one
      // program's page must not silently drop the viewer's other programs.
      const shown = new Set(programs.map((p) => p.program_id));
      const kept = (viewer.program_ids || []).filter((id) => !shown.has(id));
      const res = await orgApi.put(`/org/viewers/${viewer.admin_id}`, {
        program_ids: [...new Set([...kept, ...scope])],
        permissions: [...permissions],
      });
      toast.success("Viewer updated");
      onAdded?.(res.data?.viewer);
      onOpenChange(false);
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't update that viewer"));
    } finally {
      setSaving(false);
    }
  };

  /** Add the viewer, inviting them the way just chosen: "link" or "email". */
  const invite = async (via) => {
    setChoosing(via);
    try {
      const res = await orgApi.post("/org/viewers", {
        email: form.email.trim().toLowerCase(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        program_ids: [...scope],
        permissions: [...permissions],
        send_email: via === "email",
      });
      setAdded({ viewer: res.data?.viewer, url: res.data?.invite_url, via, emailSent: res.data?.email_sent === true });
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't add that viewer"));
      // Someone with that email already has access: the address is the fix.
      if (err?.response?.status === 409) setStep("details");
    } finally {
      setChoosing(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl font-semibold">
            {added
              ? added.via === "email" && added.emailSent
                ? "Viewer invited"
                : "Viewer added"
              : editing
                ? "Edit viewer"
                : step === "send"
                  ? "Send the invitation"
                  : "Add a viewer"}
          </DialogTitle>
        </DialogHeader>

        {added ? (
          <Invited
            name={[form.first_name.trim(), form.last_name.trim()].filter(Boolean).join(" ")}
            email={form.email.trim().toLowerCase()}
            url={added.url}
            via={added.via}
            emailSent={added.emailSent}
            programTitles={programTitles}
            firstName={form.first_name.trim()}
            onDone={close}
          />
        ) : step === "send" ? (
          <ChooseDelivery
            name={[form.first_name.trim(), form.last_name.trim()].filter(Boolean).join(" ")}
            email={form.email.trim().toLowerCase()}
            programTitles={programTitles}
            grants={permissionLabels([...permissions])}
            choosing={choosing}
            onChoose={invite}
            onBack={() => setStep("details")}
          />
        ) : (
          <form onSubmit={submit} data-testid="add-viewer-form">
            {editing ? (
              <>
                <p className="font-sans text-[14px] text-[var(--text-secondary)]" data-testid="viewer-editing">
                  {[form.first_name, form.last_name].filter(Boolean).join(" ") || form.email}
                  {form.first_name || form.last_name ? <span className="text-[var(--text-tertiary)]"> · {form.email}</span> : null}
                </p>
                {viewer.pending && viewer.active !== false && (
                  <>
                    <p className="mt-1 font-sans text-[13px] text-[var(--text-tertiary)]">
                      Hasn't set a password yet.{" "}
                      <button
                        type="button"
                        onClick={resendInvite}
                        disabled={resending || linking}
                        data-testid="viewer-resend-invite"
                        className="font-medium text-[var(--text-secondary)] hover:underline disabled:opacity-50"
                      >
                        {resending ? "Sending…" : "Send from Hans"}
                      </button>
                      {" · "}
                      <button
                        type="button"
                        onClick={newLink}
                        disabled={resending || linking}
                        data-testid="viewer-new-link"
                        className="font-medium text-[var(--text-secondary)] hover:underline disabled:opacity-50"
                      >
                        {linking ? "Making a link…" : "Share a link"}
                      </button>
                    </p>
                    {link && (
                      <InviteLink
                        url={link.url}
                        email={viewer.email}
                        firstName={viewer.first_name}
                        programTitles={programs.filter((p) => (viewer.program_ids || []).includes(p.program_id)).map((p) => p.title)}
                        note={link.note}
                      />
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                <div className="grid gap-3 sm:grid-cols-2">
                  <input value={form.first_name} onChange={set("first_name")} placeholder="First name" data-testid="viewer-first-name" className={inputClass} />
                  <input value={form.last_name} onChange={set("last_name")} placeholder="Last name" data-testid="viewer-last-name" className={inputClass} />
                </div>
                <input
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="Email"
                  aria-label="Viewer email"
                  data-testid="viewer-email"
                  className={`${inputClass} mt-3`}
                />
              </>
            )}

            <div className="mt-5">
              <div className="mb-2 font-sans text-[13px] font-medium text-[var(--text-tertiary)]">
                Which programs can they see?
              </div>
              <div className="flex flex-col gap-1">
                {programs.map((p) => (
                  <label key={p.program_id} className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={scope.has(p.program_id)}
                      onChange={() => toggleProgram(p.program_id)}
                      data-testid={`viewer-program-${p.program_id}`}
                      className="accent-[var(--accent-yellow)]"
                    />
                    <span className="font-sans text-[14px] text-[var(--text-primary)]">{p.title}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-5">
              <div className="font-sans text-[13px] font-medium text-[var(--text-tertiary)]">
                What else are they allowed to see?
              </div>
              <p className="mb-2 mt-0.5 font-sans text-[12px] text-[var(--text-tertiary)]" data-testid="viewer-baseline">
                Every viewer sees the program&rsquo;s classes and calendar.
              </p>
              <div className="flex flex-col gap-2">
                {VIEWER_PERMISSION_OPTIONS.map((o) => (
                  <label key={o.id} className="flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      checked={permissions.has(o.id)}
                      onChange={() => togglePermission(o.id)}
                      data-testid={`viewer-permission-${o.id}`}
                      className="mt-1 accent-[var(--accent-yellow)]"
                    />
                    <span>
                      <span className="font-sans text-[15px] text-[var(--text-primary)]">{o.label}</span>
                      <span className="block font-sans text-[13px] text-[var(--text-tertiary)]">{o.desc}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-5 flex items-center gap-3">
              <Button type="submit" disabled={saving} data-testid="viewer-submit">
                {editing ? (saving ? "Saving…" : "Save") : "Continue"}
              </Button>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="font-sans text-[14px] text-[var(--text-tertiary)] hover:underline"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
